import { useCallback, useRef, useState } from 'react';

import clsx from 'clsx';
import { isEmpty, isEqual } from 'lodash';
import { Editor, Node, Range, Transforms, createEditor } from 'slate';
import type { Descendant } from 'slate';
import { withHistory } from 'slate-history';
import { ReactEditor, Slate, withReact } from 'slate-react';

import { useEditorValue } from '@app/hooks/rich-text/use-editor-value';
import { useRefreshKey } from '@app/hooks/rich-text/use-refresh-key';
import { getIsContentChange } from '@flow/libs/editor/rich-text';

import { EditableElement } from './editable-element';
import { getNode } from './helpers';
import { useInlineEditableProps } from './inline-editable-context';
import style from './inline-editable.module.scss';

const CHARACTER_LIMIT = 130;

type Props = {
  readOnly: boolean;
  value: string;
  defaultValue: string;
  characterLimit: number;
  className?: string;
  editableClassName?: string;
  onChange: (newValue: string) => void;
};

export const InlineEditable = ({
  readOnly,
  value,
  defaultValue = '',
  characterLimit = CHARACTER_LIMIT,
  className,
  editableClassName,
  onChange,
}: Props) => {
  const [editor] = useState(() => withReact(withHistory(createEditor())));
  const isKeyEscape = useRef<boolean>(false);

  const { isFocused, setText, setIsFocused } = useInlineEditableProps();

  const onNodeChange = useCallback(
    (newValue: Descendant[]) => {
      setText(Node.string(newValue[0]));
    },
    [
      setText,
    ],
  );

  const currentValue = value || defaultValue;
  const initialValue = getNode(currentValue);

  const refreshKey = useRefreshKey(currentValue);

  const { editorValue, onChange: onEditorValueChange } = useEditorValue(initialValue, onNodeChange);
  const editorText = Node.string(editorValue[0]);

  const onSlateChange = (newValue: Descendant[]) => {
    const isContentChange = getIsContentChange(editor);

    if (!isContentChange) {
      return;
    }

    onEditorValueChange(newValue);
  };

  const handleSetContent = (newValue: Descendant[]) => {
    Transforms.delete(editor, {
      at: [
        0,
      ],
    });
    Transforms.insertNodes(editor, newValue, {
      at: [
        0,
      ],
    });

    onEditorValueChange(newValue);
  };

  const handleValueChange = () => {
    if (isEqual(currentValue, editorText)) {
      return;
    }

    onChange(editorText);
  };

  const getRemainingValueLength = () => {
    const { selection } = editor;
    const selectedTextLength =
      selection && Range.isExpanded(selection) ? Editor.string(editor, selection).length : 0;

    return editorText.length - selectedTextLength;
  };

  const onBeforeInput = (event: InputEvent) => {
    const text = event.data || '';
    const newTextLength = getRemainingValueLength() + text.length;

    if (newTextLength > characterLimit) {
      event.preventDefault();
    }
  };

  const onBlur = () => {
    setIsFocused(false);
    Transforms.select(editor, Editor.start(editor, []));

    if (isKeyEscape.current) {
      handleSetContent(initialValue);
      isKeyEscape.current = false;
      return;
    }

    if (isEmpty(editorText.trim())) {
      handleSetContent(getNode(defaultValue));
    }

    handleValueChange();
  };

  const onFocus = () => {
    if (!value) {
      Transforms.select(editor, {
        anchor: Editor.start(editor, []),
        focus: Editor.end(editor, []),
      });
    }

    setIsFocused(true);
  };

  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>): void => {
    if (event.key === 'Enter') {
      ReactEditor.blur(editor);
      return;
    }

    if (event.key === 'Escape') {
      isKeyEscape.current = true;
      ReactEditor.blur(editor);
    }
  };

  const onPaste = (event: React.ClipboardEvent<HTMLDivElement>) => {
    event.preventDefault();

    const { clipboardData } = event;
    const plainText = clipboardData.getData('text/plain');
    const remainingAllowedCharacters = characterLimit - getRemainingValueLength();

    if (remainingAllowedCharacters > 0) {
      const allowedText = plainText.slice(0, remainingAllowedCharacters);
      const sanitizedText = allowedText.replace(/[\r\n]+/g, ' ');

      Transforms.insertText(editor, sanitizedText);
    }
  };

  return (
    <Slate
      editor={editor}
      initialValue={initialValue}
      key={refreshKey}
      onChange={onSlateChange}
    >
      <div
        className={clsx(style.InlineEditable, className, {
          [style.Focused]: isFocused,
        })}
      >
        <EditableElement
          className={editableClassName}
          onBeforeInput={onBeforeInput}
          onBlur={onBlur}
          onFocus={onFocus}
          onKeyDown={onKeyDown}
          onPaste={onPaste}
          readOnly={readOnly}
        />
      </div>
    </Slate>
  );
};
